import { useTranslation } from 'react-i18next'
import { Bookmark } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTaggedLineItems } from '@/hooks/useTaggedLineItems'

/**
 * Tags or untags a line item for the user, so it shows under "Your tagged line
 * items" on the advertising home. Meant for the line item page's header actions.
 */
export function TagLineItemButton({ lineItemId }: { lineItemId: string }) {
  const { t } = useTranslation()
  const { isTagged, setTagged, isLoading } = useTaggedLineItems()
  const tagged = isTagged(lineItemId)
  const label = tagged ? t('lineItems.tag.untag') : t('lineItems.tag.tag')

  const toggle = async () => {
    try {
      await setTagged(lineItemId, !tagged)
    } catch (e) {
      toast.error(t('lineItems.tag.failed'), { description: (e as Error).message })
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={label}
          aria-pressed={tagged}
          disabled={isLoading}
          onClick={toggle}
          className={
            tagged
              ? 'border-destructive bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive dark:border-destructive dark:bg-destructive/15 dark:hover:bg-destructive/25'
              : undefined
          }
        >
          <Bookmark className={tagged ? 'fill-current' : undefined} />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}
